import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { EventType } from "@/features/events/types/EventType"
import { loginApi } from "@/utils/apis/auth-service"
import { useAuthStore } from "@/utils/stores/useAuthStore"
import { UnconfirmDirection, useRegistrationStore } from "@/utils/stores/useRegistrationStore"
import { useState } from "react"
import { useNavigate } from "react-router-dom"

export const useLogin = () => {
    const navigate = useNavigate()
    const { login } = useAuthStore()
    const { updateRegistrationData } = useRegistrationStore()
    const { sendEvent } = useCreateEvent()

    const [isLoading, setIsLoading] = useState(false)
    const [passwordErrorText, setPasswordErrorText] = useState("")
    const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false)

    const onBackButtonClick = async () => {
        window.open("https://www.holifya.com/", "_self")
        await sendEvent({
            event: EventType.BUTTON_CLICK,
            sub_category: "back_button",
        });
    }

    const onCreateAccountClick = async () => {
        navigate("/email-registration")
        await sendEvent({
            event: EventType.BUTTON_CLICK,
            sub_category: "action_button",
            event_metadata: { action: "create_account" }
        });
    }

    const callLogin = async (email: string, password: string) => {
        try {
            localStorage.clear()

            setIsLoading(true);
            setPasswordErrorText("");
            const result = await loginApi(email, password)
            if (result.unconfirmed) {
                await sendEvent({
                    event: EventType.LOGIN_FAILED,
                    sub_category: "unconfirmed_user",
                    event_metadata: { email },
                });
                updateRegistrationData({ registrationEmail: email, password, isUnconfirmed: UnconfirmDirection.LoginAttempt });
                navigate("/email-registration-code");
            } else if (result.passwordResetRequired) {
                await sendEvent({
                    event: EventType.LOGIN_FAILED,
                    sub_category: "password_reset_required",
                    event_metadata: { email }
                });
                setPasswordErrorText("Cambio password necessario");
                setShowChangePasswordDialog(true);
            } else {
                if (result.idToken && result.accessToken && result.userId && result.refreshToken && result.expiryTime) {
                    await sendEvent({
                        event: EventType.LOGIN_SUCCESS,
                        user_id: result.userId,
                    });
                    login(result.idToken, result.userId, result.accessToken, result.refreshToken, result.expiryTime);
                    navigate("/fetch-user-data");
                } else {
                    throw new Error("Missing token or user information in the response.");
                }
            }
        } catch (error) {
            if (error instanceof Error) {
                await sendEvent({
                    event: EventType.LOGIN_FAILED,
                    sub_category: "exception",
                    event_metadata: { email, message: error.message }
                });
                setPasswordErrorText(error.message || "Indirizzo e-mail o password errata");
            } else {
                console.error("Unexpected error type:", error);
                setPasswordErrorText("An unexpected error occurred");
            }
        } finally {
            setIsLoading(false);
        }
    }

    const handleLoginInputsSubmit = async (inputData: { [key: string]: string }) => {
        const email = inputData.email
        const password = inputData.password
        callLogin(email, password)
        await sendEvent({
            event: EventType.BUTTON_CLICK,
            sub_category: "submit_button",
            event_metadata: { action: "login", email }
        });
    }

    return {
        isLoading,
        passwordErrorText,
        showChangePasswordDialog,
        handleLoginInputsSubmit,
        setShowChangePasswordDialog,
        onBackButtonClick,
        onCreateAccountClick,
    }
}